import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Capture auth type from URL hash before Supabase cleans it on client init.
// Used to detect invite and recovery flows before onAuthStateChange fires.
const _hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
const _queryParams = new URLSearchParams(window.location.search);
// 'invite' | 'recovery' | null. Hash is what GoTrue's implicit flow returns;
// the query string covers the token_hash/PKCE link format.
export const initialUrlType = _hashParams.get('type') || _queryParams.get('type');

// Auth storage: sessionStorage by default (deliberate — shared clinic devices),
// localStorage when the user ticks "Remember me on this device" at login.
const REMEMBER_KEY = 'tbc-remember-device';

export function getRememberDevice() {
  try { return window.localStorage.getItem(REMEMBER_KEY) === '1'; } catch { return false; }
}

export function setRememberDevice(remember) {
  try {
    if (remember) {
      window.localStorage.setItem(REMEMBER_KEY, '1');
    } else {
      window.localStorage.removeItem(REMEMBER_KEY);
      // Purge any previously remembered session so a shared device can't
      // resurrect the last user's login.
      Object.keys(window.localStorage)
        .filter(k => k.startsWith('sb-'))
        .forEach(k => window.localStorage.removeItem(k));
    }
  } catch { /* storage unavailable (private browsing) — falls back to session-only */ }
}

// Safari with "Block All Cookies" or Lockdown Mode throws on any sessionStorage
// access. The link's token was still verified, so the set-password screen showed
// but the session was never kept and updateUser failed with "Auth session
// missing". Holding it in memory lets that visit finish; it just won't survive
// a reload.
const memoryStore = new Map();

const authStorage = {
  getItem: (key) => {
    try {
      if (getRememberDevice()) {
        return window.localStorage.getItem(key) ?? window.sessionStorage.getItem(key);
      }
    } catch { /* fall through to sessionStorage */ }
    try {
      return window.sessionStorage.getItem(key) ?? memoryStore.get(key) ?? null;
    } catch {
      return memoryStore.get(key) ?? null;
    }
  },
  setItem: (key, value) => {
    try {
      if (getRememberDevice()) {
        window.localStorage.setItem(key, value);
        window.sessionStorage.removeItem(key);
        return;
      }
    } catch { /* fall through to sessionStorage */ }
    try {
      window.sessionStorage.setItem(key, value);
      memoryStore.delete(key);
    } catch {
      memoryStore.set(key, value);
    }
  },
  removeItem: (key) => {
    memoryStore.delete(key);
    try { window.localStorage.removeItem(key); } catch { /* ignore */ }
    try { window.sessionStorage.removeItem(key); } catch { /* ignore */ }
  },
};

// Anything that has to last exactly as long as the session — the idle marker
// above all — belongs in the same storage the session itself uses. Split them
// and the shorter-lived half stops applying without anything looking broken:
// the marker in sessionStorage vanished when the app was closed, so a
// remembered session came back with a clean idle clock every launch.
export function authStorageArea() {
  try {
    return getRememberDevice() ? window.localStorage : window.sessionStorage;
  } catch {
    return window.sessionStorage;
  }
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    storage: authStorage,
    detectSessionInUrl: true,
  },
});

// Await a Supabase write and make failures loud. Supabase doesn't throw on
// failure — it resolves { error } — so a bare `await supabase...` inside
// try/catch reports success even when nothing was saved. Route every
// insert/update/upsert/delete/storage write through this so the surrounding
// catch actually fires and the user sees the failure.
export async function dbWrite(query, label) {
  const { data, error } = await query;
  if (error) {
    console.error(`Save failed (${label}):`, error);
    throw new Error(`${label}: ${error.message}`);
  }
  return data;
}

// The read-side twin of dbWrite. A query that fails resolves to { data: null,
// error }, and null renders as an empty list — which on these screens is a
// claim: no exercises today, nobody waiting for a reply, everyone has logged.
// Route reads through this so a failure reaches the surrounding catch and the
// screen can say it does not know, instead of inventing good news.
export async function dbRead(query, label) {
  const { data, error } = await query;
  if (error) {
    console.error(`Load failed (${label}):`, error);
    throw new Error(`${label}: ${error.message}`);
  }
  return data;
}

// How long a paused client account is kept before it's due a deletion review.
export const PAUSE_RETENTION_MONTHS = 12;

export const EXERCISE_CATEGORIES = [
  'strength',
  'impact',
  'spinal',
  'balance',
  'warmup',
  'cooldown',
];

export const CATEGORY_LABELS = {
  strength: 'Strength & Loading',
  impact: 'Impact & Bone Loading',
  spinal: 'Posture & Spinal Strength',
  balance: 'Balance & Fall Prevention',
  warmup: 'Warmup',
  cooldown: 'Cooldown & Stretch',
};

export const CATEGORY_COLORS = {
  strength: { bg: 'var(--cat-strength-bg)', text: 'var(--cat-strength-text)' },
  impact:   { bg: 'var(--cat-impact-bg)',   text: 'var(--cat-impact-text)' },
  spinal:   { bg: 'var(--cat-spinal-bg)',   text: 'var(--cat-spinal-text)' },
  balance:  { bg: 'var(--cat-balance-bg)',  text: 'var(--cat-balance-text)' },
  warmup:   { bg: 'var(--cat-warmup-bg)',   text: 'var(--cat-warmup-text)' },
  cooldown: { bg: 'var(--cat-cooldown-bg)', text: 'var(--cat-cooldown-text)' },
};

// Strong / steady / straight — the UK consensus themes (Brooke-Wavell et al.,
// Br J Sports Med 2022). These sit ABOVE the categories above, which are kept as
// the finer sub-grouping. Each exercise also carries a movement pattern and a
// load tier, so the picker can show ~23 patterns instead of 120 exercises.
export const EXERCISE_THEMES = ['strong', 'steady', 'straight'];

export const THEME_LABELS = {
  strong: 'Strong — bone loading',
  steady: 'Steady — falls prevention',
  straight: 'Straight — posture & spine',
};

export const THEME_COLORS = {
  strong:   { bg: 'var(--theme-strong-bg)',   text: 'var(--theme-strong-text)' },
  steady:   { bg: 'var(--theme-steady-bg)',   text: 'var(--theme-steady-text)' },
  straight: { bg: 'var(--theme-straight-bg)', text: 'var(--theme-straight-text)' },
};

// Movement patterns, grouped by theme. Order is the order they appear in the picker.
export const THEME_PATTERNS = {
  strong: [
    'squat', 'hinge', 'hip_extension', 'step',
    'horizontal_push', 'vertical_push', 'horizontal_pull', 'vertical_pull',
    'calf', 'carry', 'arms', 'forearm', 'impact', 'complex',
  ],
  steady: [
    'static_balance', 'dynamic_balance', 'reaching', 'hip_stability', 'movement_practice',
  ],
  straight: [
    'extension_standing', 'extension_prone', 'thoracic_mobility', 'head_neck', 'trunk',
  ],
};

export const PATTERN_LABELS = {
  squat: 'Squat',
  hinge: 'Hinge',
  hip_extension: 'Hip extension (supine)',
  step: 'Step & single leg',
  horizontal_push: 'Horizontal push',
  vertical_push: 'Vertical push',
  // Pull was a single pattern until Aug 2026. Push had always been split, so a
  // programme with only rows in it read as "pull: covered" and a missing
  // vertical pull was invisible — which is how Strong for Life — Advanced
  // shipped without one.
  horizontal_pull: 'Horizontal pull',
  vertical_pull: 'Vertical pull',
  calf: 'Calf & ankle',
  carry: 'Loaded carry',
  arms: 'Arms (accessory)',
  forearm: 'Forearm loading',
  impact: 'Impact',
  complex: 'Complex / method',
  static_balance: 'Static balance',
  dynamic_balance: 'Dynamic balance & gait',
  reaching: 'Reaching & limits of stability',
  hip_stability: 'Hip stability',
  movement_practice: 'Movement practice',
  extension_standing: 'Spinal extension (standing)',
  extension_prone: 'Spinal extension (prone)',
  thoracic_mobility: 'Thoracic mobility',
  head_neck: 'Head & neck position',
  trunk: 'Trunk & core control',
};

// Load tier. 4 is supervised-only: LIFTMOR's safety finding was conditioned on
// "highly supervised conditions", so it never goes on a home programme.
export const TIER_LABELS = {
  1: 'Pattern / supported',
  2: 'Bodyweight or light load',
  3: 'Loaded',
  4: 'Heavy — supervised only',
};

// Impact tier 1 is the brisk-walking ceiling for anyone with a vertebral or
// multiple low-trauma fracture.
export const IMPACT_TIER_NOTES = {
  1: 'Up to brisk walking',
  2: 'Moderate impact',
  3: 'Full impact',
  4: 'High force',
};

// How load is expressed. Shared by the sessions screen (session_entries) and by
// programme prescriptions (programme_exercises), so a band exercise can't be
// given a kg figure in one place and a colour in the other.
// Deliberately literal hex, and deliberately NOT tokens. These are the colours
// of physical resistance bands: a yellow band is yellow whatever the brand
// palette does. Re-theming the app must not repaint them.
export const BANDS = [
  { id: 'yellow', hex: '#e3c419' },
  { id: 'red',    hex: '#c0392b' },
  { id: 'green',  hex: '#4a7c59' },
  { id: 'blue',   hex: '#3d578a' },
  { id: 'black',  hex: '#333333' },
];
export const BAND_RANK = { yellow: 0, red: 1, green: 2, blue: 3, black: 4 };

// Sessions cycle through the first three; prescriptions add 'none' for the
// time- and count-based work (balance holds, impact drills) where a load box is
// just something else to leave blank.
export const PRESCRIPTION_LOAD_MODES = ['kg', 'band', 'technique', 'none'];

export const LOAD_MODE_LABELS = {
  kg: 'Weight / load',
  band: 'Band',
  technique: 'Bodyweight',
  none: 'No load',
};

export const LOAD_MODE_CHIPS = { kg: 'KG', band: 'Band', technique: 'Body', none: 'None' };

// A prescription inherits the exercise's default unless it overrides it.
// Null on both behaves as kg — the pre-existing free-text weight box.
export const effectiveLoadMode = (pe, exercise) =>
  pe?.load_mode || exercise?.default_load_mode || pe?.exercise?.default_load_mode || 'kg';

// What the client is shown for a prescription's load, e.g. "red band" | "10kg".
export function prescriptionLoadLabel(pe, exercise) {
  const mode = effectiveLoadMode(pe, exercise);
  if (mode === 'none') return null;
  if (mode === 'technique') return 'Bodyweight';
  if (mode === 'band') return pe?.resistance ? `${pe.resistance} band` : 'Resistance band';
  return pe?.weight || exercise?.default_weight || pe?.exercise?.default_weight || null;
}

// Client categories — how a client is grouped in the therapist views, and what
// warmup presets they're gated against (warmup_templates.contraindicated_categories).
// Ordered least to most bone loss, then the non-bone-health groups.
export const CLIENT_CATEGORIES = [
  'Osteoporosis',
  'Osteopenia',
  'Bone Health',
  'Strength Training',
  'General',
];

// The bone-health categories, strictest first. A client outside this list is on
// the strength track and gets no diagnosis-driven warnings in the picker.
export const BONE_HEALTH_CATEGORIES = ['Osteoporosis', 'Osteopenia', 'Bone Health'];

// ─────────────────────────────────────────────────────────────────────────────
// Equipment — what an exercise needs to be done at all.
//
// This, not the exercise list, is what separates a clinic S&C programme from a
// home bone-health one. The exercises are shared: an osteoporosis client should
// be deadlifting too (Brooke-Wavell et al. 2022, LIFTMOR). Ordered from "any
// kitchen" to "needs the clinic".
// ─────────────────────────────────────────────────────────────────────────────
export const EQUIPMENT = [
  'bodyweight', 'household', 'band', 'wrist_weights', 'balance_kit', 'pole',
  'step', 'bench', 'pull_up_bar', 'suspension', 'dumbbell', 'kettlebell', 'barbell',
  'trap_bar', 'cable', 'landmine', 'machine',
];

export const EQUIPMENT_LABELS = {
  bodyweight:    'Bodyweight',
  household:     'Wall / worktop / chair',
  band:          'Resistance band',
  wrist_weights: 'Wrist weights',
  balance_kit:   'Foam pad / roller / ladder',
  pole:          'Pole',
  step:          'Step or box',
  bench:         'Bench',
  pull_up_bar:   'Pull-up bar',
  suspension:    'Suspension trainer',
  dumbbell:      'Dumbbells',
  kettlebell:    'Kettlebell',
  barbell:       'Barbell',
  trap_bar:      'Trap bar',
  cable:         'Cable machine',
  landmine:      'Landmine',
  machine:       'Resistance machine',
};

// Needs the clinic or a gym. Everything else can go on a home programme.
export const GYM_EQUIPMENT = ['barbell', 'trap_bar', 'cable', 'landmine', 'kettlebell', 'machine'];

export const needsGym = exercise =>
  (exercise?.equipment || []).some(e => GYM_EQUIPMENT.includes(e));

// ─────────────────────────────────────────────────────────────────────────────
// Prescription warnings.
//
// Risk flags, not an audience split. Most of these come from the structural
// columns the library already carries, so all 120 exercises are covered without
// anyone tagging them one by one; contraindicated_categories is the manual
// exception list for whatever the rules miss.
//
// Every warning is soft. Carole is the clinician and the same exercise is
// usually fine with a cue, so the picker asks her to confirm rather than hiding
// anything.
// ─────────────────────────────────────────────────────────────────────────────
export function exerciseWarnings(exercise, clientCategory) {
  if (!exercise) return [];
  const cat = clientCategory || '';
  const boneHealth = BONE_HEALTH_CATEGORIES.includes(cat);
  const out = [];

  if ((exercise.contraindicated_categories || []).includes(cat)) {
    out.push({
      key: 'contraindicated',
      level: 'block',
      short: 'Not for this client',
      text: `Flagged in the library as not for ${cat} clients.`,
    });
  }

  // LIFTMOR's safety finding was conditioned on highly supervised conditions,
  // so tier 4 never goes on a home programme whoever the client is.
  if (exercise.supervised_only) {
    out.push({
      key: 'supervised',
      level: 'warn',
      short: 'Supervised only',
      text: 'Heavy tier — class or 1:1 only, not something to send home.',
    });
  }

  if (boneHealth && exercise.requires_hinge_cue) {
    out.push({
      key: 'hinge',
      level: 'warn',
      short: 'Hinge cue needed',
      text: 'Needs the hip-hinge cue. Loaded spinal flexion is the thing to avoid, not the lift.',
    });
  }

  // Tier 1 impact is the brisk-walking ceiling for anyone with a vertebral or
  // multiple low-trauma fracture, so it's fracture history that decides this.
  // Diagnosis is the closest proxy the app holds.
  const impactCeiling = cat === 'Osteoporosis' ? 1 : cat === 'Osteopenia' ? 2 : null;
  if (impactCeiling && exercise.pattern === 'impact' && (exercise.tier || 0) > impactCeiling) {
    out.push({
      key: 'impact',
      level: 'warn',
      short: 'Above impact ceiling',
      text: `${IMPACT_TIER_NOTES[exercise.tier] || 'Higher impact'} — check there's no vertebral or repeated low-trauma fracture first.`,
    });
  }

  return out;
}

// The single worst level across an exercise's warnings, for the picker chip.
export const warningLevel = warnings =>
  warnings.some(w => w.level === 'block') ? 'block' : warnings.length ? 'warn' : null;

// Information Library article categories — a separate palette from the exercise
// categories above (different names, same visual treatment for consistency).
export const INFO_CATEGORY_COLORS = {
  'General':         { bg: 'var(--info-general-bg)',      text: 'var(--info-general-text)' },
  'Osteoporosis':    { bg: 'var(--info-osteoporosis-bg)', text: 'var(--info-osteoporosis-text)' },
  'Osteopenia':      { bg: 'var(--info-osteopenia-bg)',   text: 'var(--info-osteopenia-text)' },
  'Exercise Tips':   { bg: 'var(--info-tips-bg)',         text: 'var(--info-tips-text)' },
  'Bone Health':     { bg: 'var(--info-bone-bg)',         text: 'var(--info-bone-text)' },
  'Nutrition':       { bg: 'var(--info-nutrition-bg)',    text: 'var(--info-nutrition-text)' },
  'Lifestyle':       { bg: 'var(--info-lifestyle-bg)',    text: 'var(--info-lifestyle-text)' },
  'Pain Management': { bg: 'var(--info-pain-bg)',         text: 'var(--info-pain-text)' },
  'Home Care':       { bg: 'var(--info-home-bg)',         text: 'var(--info-home-text)' },
};
